'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';

// ─── TYPES ────────────────────────────────────────────────────────────────────

type QuestStatus = 'open' | 'active' | 'completed' | 'archived';
type QuestType = 'iftar-wheels' | 'ward-bridge' | 'observation-action' | 'resource-chain' | 'khidmah';

interface AsbabQuest {
  id: string;
  type: QuestType;
  title: string;
  arabic: string;
  icon: string;
  color: string;
  desc: string;
  irlLayer: string;
  irlName: string;
  mrgLink: string;
  mrgDisparity: string;
  city: string;
  ward: string;
  status: QuestStatus;
  caretakers: number;
  resourcesNeeded: string[];
  resourcesAvailable: string[];
  observationChain: string[];
  actionChain: string[];
  impact: { metric: string; value: string; color: string }[];
  amanah: string;
}

interface ImpactEntry {
  date: string;
  quest: string;
  action: string;
  caretaker: string;
  impact: string;
}

// ─── QUEST DATA ───────────────────────────────────────────────────────────────

const QUESTS: AsbabQuest[] = [
  {
    id: 'q1',
    type: 'iftar-wheels',
    title: 'Iftar on Wheels',
    arabic: 'إِفْطَار عَلَى الْعَجَلَات',
    icon: '🌙',
    color: '#c9a84c',
    desc: 'Coordinate Iftar meal distribution to homebound, elderly, and incarcerated community members during Ramadan. Link available food resources from the MRG with caretakers who can deliver fisabilillah.',
    irlLayer: 'Amanah — Layer 7',
    irlName: 'Ar-Razzaq — The Provider',
    mrgLink: 'Food Resources · Your Ward',
    mrgDisparity: 'Food insecurity among homebound elderly — 23% of Muslim seniors lack access to halal Iftar during Ramadan.',
    city: 'Chicago',
    ward: 'South Side',
    status: 'active',
    caretakers: 12,
    resourcesNeeded: ['Halal meals (50/night)', 'Delivery vehicles (3)', 'Volunteer drivers (8)', 'Packaging containers'],
    resourcesAvailable: ['Masjid Al-Faatir kitchen (confirmed)', 'IMAN food pantry (confirmed)', '4 volunteer drivers (confirmed)'],
    observationChain: [
      'Zahir: Elderly neighbor unable to leave home during Ramadan',
      'Sunnat Allah: The pattern of communal care in Islamic tradition — feeding the fasting is Sunnah',
      'Haqq: This person is owed care by the community — Amanah of the neighborhood',
      'Amanah: I am responsible for bridging this gap with available resources',
      'Uboor: Organize delivery route, connect with masjid kitchen, recruit drivers',
    ],
    actionChain: [
      'Map homebound community members in ward',
      'Contact MRG-listed food resources for meal donations',
      'Recruit caretaker drivers through community network',
      'Establish delivery routes and schedule',
      'Document impact and share with MRG collection',
    ],
    impact: [
      { metric: 'Meals Delivered', value: '340', color: '#c9a84c' },
      { metric: 'Households Served', value: '68', color: '#2d7a47' },
      { metric: 'Caretakers Active', value: '12', color: '#2d8a87' },
      { metric: 'Wards Covered', value: '3', color: '#8a8ab8' },
    ],
    amanah: 'The meal is the Amanah made visible. Every delivery is an act of Uboor — crossing from recognition to service.',
  },
  {
    id: 'q2',
    type: 'ward-bridge',
    title: 'Ward Resource Bridge',
    arabic: 'جِسْر الْمَوَارِد',
    icon: '⬡',
    color: '#2d7a47',
    desc: 'Identify resource gaps in your ward using MRG disparity data, then bridge caretakers with available resources fisabilillah. The IRL Lens reveals the Amanah; the MRG reveals the means.',
    irlLayer: 'Sunnat Allah — Layer 3',
    irlName: 'Al-Adl — The Just',
    mrgLink: 'Disparities · Your Ward',
    mrgDisparity: 'Healthcare desert in Englewood — no primary care within 2 miles. 40% of residents lack transportation to distant clinics.',
    city: 'Chicago',
    ward: 'Englewood',
    status: 'open',
    caretakers: 5,
    resourcesNeeded: ['Transportation volunteers (10)', 'Healthcare navigation support', 'Community health workers', 'Translation services (Somali, Arabic)'],
    resourcesAvailable: ['IMAN Health Clinic (confirmed)', '2 volunteer drivers', 'Arabic-speaking community health worker'],
    observationChain: [
      'Zahir: Community members traveling 45 minutes by bus for basic healthcare',
      'Sunnat Allah: The pattern of communal responsibility — the ummah as a single body',
      'Haqq: Healthcare access is a right; the disparity is a violation of Al-Adl',
      'Amanah: The caretaker who sees this gap is responsible for bridging it',
      'Uboor: Organize transportation network, connect with IMAN clinic, train navigators',
    ],
    actionChain: [
      'Document healthcare access gaps using IRL Lens observation',
      'Submit disparity to MRG collection for ward steward review',
      'Recruit transportation volunteers from masjid network',
      'Partner with IMAN clinic for community health days',
      'Track impact: appointments made, transportation provided',
    ],
    impact: [
      { metric: 'Rides Provided', value: '0', color: '#2d7a47' },
      { metric: 'Appointments Made', value: '0', color: '#2d8a87' },
      { metric: 'Caretakers Recruited', value: '5', color: '#c9a84c' },
      { metric: 'Wards Mapped', value: '1', color: '#8a8ab8' },
    ],
    amanah: 'The bridge is the Amanah. Every gap you see is a trust placed in you by the One who showed it to you.',
  },
  {
    id: 'q3',
    type: 'observation-action',
    title: 'Observation → Action',
    arabic: 'مُلَاحَظَة إِلَى عَمَل',
    icon: '◎',
    color: '#2d8a87',
    desc: 'Complete a full IRL Lens observation in your ward, identify the Amanah it reveals, then take one concrete action fisabilillah. The observation chain becomes the action chain.',
    irlLayer: 'Uboor — Layer 8',
    irlName: 'Al-Basir — The All-Seeing',
    mrgLink: 'Context · Your Ward',
    mrgDisparity: 'Youth unemployment at 35% in the district — young Muslims without vocational pathways aligned with Islamic values.',
    city: 'Houston',
    ward: 'Mahatma Gandhi District',
    status: 'open',
    caretakers: 18,
    resourcesNeeded: ['Vocational mentors (5)', 'Halal-friendly employer contacts', 'Youth programming space', 'Islamic finance guidance'],
    resourcesAvailable: ['Local masjid space (confirmed)', '2 Muslim business owners willing to mentor', 'ISNA vocational resources'],
    observationChain: [
      'Zahir: Young Muslims idle in the neighborhood, no visible vocational activity',
      'Sunnat Allah: The pattern of purposeful creation — every human being has a vocation',
      'Haqq: These youth have gifts and potential that the community has not yet activated',
      'Amanah: The community is responsible for creating pathways for its youth',
      'Uboor: Develop vocational mentorship program rooted in Islamic values and local needs',
    ],
    actionChain: [
      'Use IRL Lens to document youth needs and gifts in the ward',
      'Map available vocational resources in MRG',
      'Connect youth with Muslim business mentors',
      'Develop curriculum integrating Islamic values with vocational skills',
      'Track employment outcomes and document as community impact',
    ],
    impact: [
      { metric: 'Youth Engaged', value: '0', color: '#2d8a87' },
      { metric: 'Mentors Matched', value: '2', color: '#2d7a47' },
      { metric: 'Observations Filed', value: '7', color: '#c9a84c' },
      { metric: 'Actions Taken', value: '3', color: '#8a8ab8' },
    ],
    amanah: 'The observation is the beginning. The action is the Uboor. Document both — they are the living record of the caretaker\'s path.',
  },
  {
    id: 'q4',
    type: 'khidmah',
    title: 'Khidmah Fisabilillah',
    arabic: 'خِدْمَة فِي سَبِيلِ اللَّه',
    icon: '✦',
    color: '#8a8ab8',
    desc: 'Perform one act of service in your ward and document it as an Amanah fulfilled. Link your service to an IRL Lens observation and an MRG resource. The khidmah becomes a node in the community impact network.',
    irlLayer: 'Amanah — Layer 7',
    irlName: 'Ar-Rahman — The Most Gracious',
    mrgLink: 'Community Context · Your Ward',
    mrgDisparity: 'Isolation among recently released formerly incarcerated Muslims — lack of reintegration support and community connection.',
    city: 'Detroit',
    ward: 'Dearborn',
    status: 'active',
    caretakers: 9,
    resourcesNeeded: ['Reintegration mentors (4)', 'Housing navigation support', 'Employment connections', 'Spiritual support network'],
    resourcesAvailable: ['ACCESS social services (confirmed)', 'Local masjid reintegration program', '3 volunteer mentors'],
    observationChain: [
      'Zahir: Formerly incarcerated community member struggling to reconnect after release',
      'Sunnat Allah: The pattern of return and restoration — tawbah as a communal process',
      'Haqq: This person is owed welcome and support by the community',
      'Amanah: The caretaker who sees this isolation is responsible for bridging it',
      'Uboor: Connect with ACCESS, recruit mentors, establish welcome network',
    ],
    actionChain: [
      'Document isolation patterns using IRL Lens',
      'Connect with ACCESS and local masjid reintegration programs',
      'Recruit and train volunteer mentors',
      'Establish regular check-in and support structure',
      'Document reintegration outcomes for MRG collection',
    ],
    impact: [
      { metric: 'People Supported', value: '14', color: '#8a8ab8' },
      { metric: 'Mentors Active', value: '9', color: '#2d7a47' },
      { metric: 'Connections Made', value: '31', color: '#2d8a87' },
      { metric: 'Months Active', value: '4', color: '#c9a84c' },
    ],
    amanah: 'Khidmah fisabilillah is the Amanah made visible in the world. Every act of service is a sign — an Ayah in the community.',
  },
];

const IMPACT_LOG: ImpactEntry[] = [
  { date: '2026-03-19', quest: 'Iftar on Wheels', action: 'Delivered 28 Iftar meals to homebound elderly in South Side', caretaker: 'caretaker_chicago_01', impact: '28 meals · 6 households' },
  { date: '2026-03-18', quest: 'Khidmah Fisabilillah', action: 'Connected 3 formerly incarcerated brothers with ACCESS employment services', caretaker: 'caretaker_detroit_01', impact: '3 referrals · 2 interviews scheduled' },
  { date: '2026-03-17', quest: 'Iftar on Wheels', action: 'Recruited 2 new volunteer drivers from Masjid Al-Faatir', caretaker: 'caretaker_chicago_02', impact: '2 drivers · 40 additional meals/week capacity' },
  { date: '2026-03-16', quest: 'Observation → Action', action: 'Filed IRL Lens observation on youth unemployment in Mahatma Gandhi District', caretaker: 'caretaker_houston_01', impact: '1 observation filed · 5 youth identified' },
  { date: '2026-03-15', quest: 'Ward Resource Bridge', action: 'Submitted healthcare disparity to MRG for Englewood ward', caretaker: 'caretaker_chicago_03', impact: '1 disparity documented · pending steward review' },
];

// ─── QUEST CARD ───────────────────────────────────────────────────────────────

function QuestCard({ quest, onJoin }: { quest: AsbabQuest; onJoin: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState<'overview' | 'chain' | 'resources' | 'impact'>('overview');

  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${quest.color}25`, borderTop: `3px solid ${quest.color}`, borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
      {/* Quest header */}
      <div style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 12 }} onClick={() => setExpanded(v => !v)}>
        <span style={{ fontSize: 22, flexShrink: 0 }}>{quest.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 7, padding: '2px 8px', borderRadius: 10, background: quest.status === 'active' ? `${quest.color}20` : 'rgba(255,255,255,0.04)', border: `1px solid ${quest.status === 'active' ? quest.color + '50' : 'rgba(255,255,255,0.08)'}`, color: quest.status === 'active' ? quest.color : 'rgba(212,197,169,0.3)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
              {quest.status}
            </span>
            <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)' }}>{quest.city} · {quest.ward}</span>
            <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)' }}>👥 {quest.caretakers} caretakers</span>
          </div>
          <h3 style={{ fontFamily: 'monospace', fontSize: 14, color: '#F5F0E8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.02em', marginBottom: 2 }}>{quest.title}</h3>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: `${quest.color}70`, marginBottom: 4 }}>{quest.arabic}</p>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.6 }}>{quest.desc.slice(0, 120)}...</p>
        </div>
        <span style={{ color: 'rgba(212,197,169,0.3)', fontSize: 14, flexShrink: 0 }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{ borderTop: `1px solid ${quest.color}15` }}>
          {/* Section tabs */}
          <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${quest.color}10` }}>
            {(['overview', 'chain', 'resources', 'impact'] as const).map(s => (
              <button key={s} onClick={() => setActiveSection(s)} style={{ flex: 1, padding: '10px 8px', background: activeSection === s ? `${quest.color}10` : 'transparent', border: 'none', borderBottom: `2px solid ${activeSection === s ? quest.color : 'transparent'}`, color: activeSection === s ? quest.color : 'rgba(212,197,169,0.4)', fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}>
                {s === 'chain' ? 'Obs→Action' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          <div style={{ padding: '16px 18px' }}>
            {activeSection === 'overview' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 12, lineHeight: 1.7 }}>{quest.desc}</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ padding: '10px 12px', background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 6 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d8a87', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>IRL Lens Link</p>
                    <p style={{ fontFamily: 'monospace', fontSize: 10, color: '#F5F0E8', fontWeight: 700, marginBottom: 2 }}>{quest.irlName}</p>
                    <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 9 }}>{quest.irlLayer}</p>
                  </div>
                  <div style={{ padding: '10px 12px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 6 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d7a47', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>MRG Disparity</p>
                    <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 10, lineHeight: 1.5 }}>{quest.mrgDisparity.slice(0, 80)}...</p>
                  </div>
                </div>
                <div style={{ padding: '12px 14px', background: `${quest.color}06`, border: `1px solid ${quest.color}20`, borderRadius: 6 }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, color: `${quest.color}80`, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Amanah</p>
                  <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 11, lineHeight: 1.7, fontStyle: 'italic' }}>"{quest.amanah}"</p>
                </div>
                <button onClick={() => onJoin(quest.id)} style={{ padding: '10px 20px', background: `${quest.color}15`, border: `1px solid ${quest.color}45`, borderRadius: 8, color: quest.color, fontFamily: 'monospace', fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s', alignSelf: 'flex-start' }}>
                  ⊕ Join This Quest
                </button>
              </div>
            )}

            {activeSection === 'chain' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Observation Chain → Action Chain</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d8a87', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>◎ IRL Lens Observations</p>
                    {quest.observationChain.map((obs, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(45,138,135,0.15)', border: '1px solid rgba(45,138,135,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'monospace', fontSize: 8, color: '#2d8a87' }}>{i + 1}</div>
                        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 10, lineHeight: 1.6 }}>{obs}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>⬡ Action Chain</p>
                    {quest.actionChain.map((action, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(45,122,71,0.15)', border: '1px solid rgba(45,122,71,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'monospace', fontSize: 8, color: '#2d7a47' }}>{i + 1}</div>
                        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 10, lineHeight: 1.6 }}>{action}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'resources' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#e05a5a', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>⚠ Resources Needed</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {quest.resourcesNeeded.map((r, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: 'rgba(180,80,80,0.06)', border: '1px solid rgba(180,80,80,0.15)', borderRadius: 6 }}>
                        <span style={{ color: '#e05a5a', fontSize: 10 }}>○</span>
                        <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 10 }}>{r}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>✓ Resources Available</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {quest.resourcesAvailable.map((r, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 6 }}>
                        <span style={{ color: '#2d7a47', fontSize: 10 }}>✓</span>
                        <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 10 }}>{r}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <Link href="/mrg-embed/collection" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.3)', borderRadius: 6, color: '#2d7a47', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', textDecoration: 'none', alignSelf: 'flex-start' }}>
                  ⬡ Find More Resources in MRG →
                </Link>
              </div>
            )}

            {activeSection === 'impact' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                  {quest.impact.map((imp, i) => (
                    <div key={i} style={{ textAlign: 'center', padding: '12px 8px', background: `${imp.color}08`, border: `1px solid ${imp.color}20`, borderRadius: 8 }}>
                      <p style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 900, color: imp.color, marginBottom: 4 }}>{imp.value}</p>
                      <p style={{ fontFamily: 'monospace', fontSize: 7, color: `${imp.color}70`, textTransform: 'uppercase', letterSpacing: '.08em' }}>{imp.metric}</p>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '12px 14px', background: `${quest.color}06`, border: `1px solid ${quest.color}15`, borderRadius: 6 }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, color: `${quest.color}70`, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Community Impact Statement</p>
                  <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.7 }}>
                    This quest links IRL Lens fission outputs directly to MRG disparity data, creating an observation-to-action chain that transforms recognition into beneficial service. Each caretaker who joins adds to the community impact network.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function AsbabQuestPage() {
  const [quests, setQuests] = useState(QUESTS);
  const [joinedQuests, setJoinedQuests] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'quests' | 'impact' | 'submit'>('quests');
  const [filterCity, setFilterCity] = useState('all');
  const [newObs, setNewObs] = useState('');
  const [obsLayer, setObsLayer] = useState('Zahir');
  const [obsChain, setObsChain] = useState<string[]>([]);

  const handleJoin = (id: string) => {
    setJoinedQuests(prev => prev.includes(id) ? prev.filter(q => q !== id) : [...prev, id]);
    setQuests(prev => prev.map(q => q.id === id ? { ...q, caretakers: joinedQuests.includes(id) ? q.caretakers - 1 : q.caretakers + 1 } : q));
  };

  const cities = ['all', ...Array.from(new Set(quests.map(q => q.city)))];
  const filteredQuests = filterCity === 'all' ? quests : quests.filter(q => q.city === filterCity);

  return (
    <div style={{ background: '#060d12', minHeight: '100vh' }}>
      <Header />
      <main style={{ maxWidth: 1200, margin: '0 auto', padding: 'clamp(80px, 15vw, 96px) clamp(12px, 4vw, 24px) clamp(48px, 8vw, 64px)' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(212,168,42,0.5)', marginBottom: 6 }}>⌘ Asbab Quest Interface</p>
          <h1 style={{ fontFamily: 'monospace', fontSize: 'clamp(20px, 5vw, 28px)', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '-0.02em', color: '#F5F0E8', marginBottom: 4 }}>
            Asbab Quest
          </h1>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: 'rgba(212,168,42,0.6)', marginBottom: 8 }}>قُوَّة الأَسْبَاب — The Power of Means</p>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, lineHeight: 1.7, maxWidth: 700 }}>
            Operationable Uboor quests that link the Inner Reality Lens and Muslim Resource Guide with caretakers, their community, and beneficial work to be done in a given locality based on available resources fisabilillah.
          </p>
        </div>

        {/* IRL → MRG bridge banner */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 10, alignItems: 'center', marginBottom: 28, padding: '14px 18px', background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10 }}>
          <div style={{ padding: '10px 14px', background: 'rgba(45,138,135,0.08)', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 8, textAlign: 'center' }}>
            <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d8a87', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>◎ IRL Lens</p>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 10 }}>Fission sequences reveal Amanah in the community</p>
          </div>
          <div style={{ textAlign: 'center', padding: '0 8px' }}>
            <p style={{ fontFamily: 'monospace', fontSize: 14, color: 'rgba(212,168,42,0.6)' }}>⌘</p>
            <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)', letterSpacing: '.08em', textTransform: 'uppercase' }}>Asbab</p>
          </div>
          <div style={{ padding: '10px 14px', background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 8, textAlign: 'center' }}>
            <p style={{ fontFamily: 'monospace', fontSize: 8, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>⬡ MRG</p>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 10 }}>Disparities and resources mapped for your ward</p>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 28 }}>
          {[
            { label: 'Active Quests', value: quests.filter(q => q.status === 'active').length, color: '#c9a84c' },
            { label: 'Open Quests', value: quests.filter(q => q.status === 'open').length, color: '#2d8a87' },
            { label: 'Caretakers', value: quests.reduce((a, q) => a + q.caretakers, 0), color: '#2d7a47' },
            { label: 'Cities', value: new Set(quests.map(q => q.city)).size, color: '#8a8ab8' },
          ].map(stat => (
            <div key={stat.label} style={{ background: `${stat.color}08`, border: `1px solid ${stat.color}20`, borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
              <p style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 900, color: stat.color }}>{stat.value}</p>
              <p style={{ fontFamily: 'monospace', fontSize: 7, color: `${stat.color}70`, textTransform: 'uppercase', letterSpacing: '.08em' }}>{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: 4, width: 'fit-content' }}>
          {([
            { id: 'quests', label: 'Quests', icon: '⌘' },
            { id: 'impact', label: 'Impact Log', icon: '◎' },
            { id: 'submit', label: 'New Observation', icon: '⊕' },
          ] as const).map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '8px 14px', background: activeTab === tab.id ? 'rgba(212,168,42,0.12)' : 'transparent', border: `1px solid ${activeTab === tab.id ? 'rgba(212,168,42,0.4)' : 'transparent'}`, borderRadius: 6, color: activeTab === tab.id ? '#D4A82A' : 'rgba(212,197,169,0.4)', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'quests' && (
          <div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <select value={filterCity} onChange={e => setFilterCity(e.target.value)} style={{ background: 'rgba(6,13,18,0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '6px 10px', color: '#F5F0E8', fontSize: 10, fontFamily: 'monospace', cursor: 'pointer' }}>
                {cities.map(c => <option key={c} value={c}>{c === 'all' ? 'All Cities' : c}</option>)}
              </select>
              <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.06em' }}>
                {filteredQuests.length} quest{filteredQuests.length !== 1 ? 's' : ''} · Click to expand
              </p>
            </div>
            {filteredQuests.map(quest => <QuestCard key={quest.id} quest={quest} onJoin={handleJoin} />)}
          </div>
        )}

        {activeTab === 'impact' && (
          <div>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 16 }}>Community Impact Log</p>
            {IMPACT_LOG.map((entry, i) => (
              <div key={i} style={{ padding: '12px 16px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderLeft: '3px solid rgba(45,122,71,0.5)', borderRadius: 8, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)' }}>{entry.date}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 8, padding: '2px 8px', borderRadius: 4, background: 'rgba(212,168,42,0.1)', border: '1px solid rgba(212,168,42,0.25)', color: '#D4A82A', textTransform: 'uppercase', letterSpacing: '.06em' }}>{entry.quest}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)' }}>by {entry.caretaker}</span>
                </div>
                <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 11, lineHeight: 1.6, marginBottom: 4 }}>{entry.action}</p>
                <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#2d7a47', letterSpacing: '.06em' }}>Impact: {entry.impact}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'submit' && (
          <div style={{ maxWidth: 600 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 16 }}>
              New IRL Lens Observation → Asbab Quest Seed
            </p>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
              {['Zahir', 'Sunnat Allah', 'Haqq', 'Amanah', 'Uboor'].map(l => (
                <button key={l} onClick={() => setObsLayer(l)} style={{ padding: '5px 12px', background: obsLayer === l ? 'rgba(45,138,135,0.2)' : 'transparent', border: `1px solid ${obsLayer === l ? 'rgba(45,138,135,0.5)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 6, color: obsLayer === l ? '#2d8a87' : 'rgba(212,197,169,0.4)', fontFamily: 'monospace', fontSize: 8, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer' }}>
                  {l}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <input value={newObs} onChange={e => setNewObs(e.target.value)} placeholder={`${obsLayer} observation in your ward...`} style={{ flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(45,138,135,0.25)', borderRadius: 6, padding: '8px 12px', color: '#F5F0E8', fontSize: 11, fontFamily: 'monospace', outline: 'none' }} />
              <button onClick={() => { if (newObs.trim()) { setObsChain(c => [`[${obsLayer}] ${newObs}`, ...c]); setNewObs(''); } }} style={{ padding: '8px 14px', background: 'rgba(45,138,135,0.12)', border: '1px solid rgba(45,138,135,0.35)', borderRadius: 6, color: '#2d8a87', fontFamily: 'monospace', fontSize: 9, cursor: 'pointer' }}>⊕</button>
            </div>
            {obsChain.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {obsChain.map((obs, i) => (
                  <div key={i} style={{ padding: '8px 12px', background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.15)', borderRadius: 6, marginBottom: 5 }}>
                    <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 10, lineHeight: 1.5 }}>{obs}</p>
                  </div>
                ))}
                <button onClick={() => { setObsChain([]); }} style={{ marginTop: 10, padding: '8px 16px', background: 'rgba(212,168,42,0.1)', border: '1px solid rgba(212,168,42,0.3)', borderRadius: 6, color: '#D4A82A', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer' }}>
                  ⌘ Seed as Asbab Quest
                </button>
              </div>
            )}
            <div style={{ padding: '14px 16px', background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 8 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.08em', lineHeight: 1.7 }}>
                Your observation chain will be reviewed by a ward steward and can be seeded as a new Asbab Quest for caretakers in your locality. Link it to an MRG disparity to create a full observation-to-action chain.
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 32, padding: '16px 20px', background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.08em' }}>
            Asbab Quest · The Haal Project · haalproject.org
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <Link href="/inner-reality-lens" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(45,138,135,0.6)', textDecoration: 'none', padding: '4px 10px', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 4 }}>IRL Lens →</Link>
            <Link href="/muslim-resource-guide" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.6)', textDecoration: 'none', padding: '4px 10px', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 4 }}>MRG →</Link>
            <Link href="/user-dashboard" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.6)', textDecoration: 'none', padding: '4px 10px', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 4 }}>My Garden →</Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
